import React, { useState } from 'react';
import { usePartsSearch } from '../../hooks/usePartsSearch';
import { YEARS, PART_CATEGORIES_LIST } from '../../data/siteData';

function SelectInput({ value, onChange, children, dark = false, style = {} }) {
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      className={dark ? 'form-input form-input--dark' : 'form-input'}
      style={{ ...style }}
    >
      {children}
    </select>
  );
}

function TextInput({ value, onChange, placeholder, dark = false }) {
  return (
    <input
      type="text"
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className={dark ? 'form-input form-input--dark' : 'form-input'}
      onKeyDown={e => e.key === 'Enter' && onChange(value)}
    />
  );
}

export function PartsSearchForm({ dark = false, compact = false, onSearch }) {
  const [activeTab, setActiveTab] = useState('vehicle');
  const [error, setError] = useState('');
  const ps = usePartsSearch();

  const handleVehicleSearch = () => {
    setError('');
    if (!ps.year && !ps.make && !ps.partName) {
      setError('Please select a year, make, or enter a part category.');
      return;
    }
    const success = ps.search();
    if (!success) setError('Please enter at least one search criteria.');
    if (onSearch) onSearch({ year: ps.year, make: ps.make, model: ps.model, category: ps.category });
  };

  const handlePartSearch = () => {
    setError('');
    if (!ps.partName.trim()) {
      setError('Please enter a part name or number.');
      return;
    }
    ps.searchByPartName();
  };

  const inputBg = dark ? 'rgba(255,255,255,0.08)' : 'var(--ice)';
  const labelColor = dark ? 'rgba(255,255,255,0.6)' : 'var(--text-mid)';
  const tabActiveBorder = 'var(--orange)';
  const tabColor = dark ? 'white' : 'var(--navy)';
  const tabBorder = dark ? '1px solid rgba(255,255,255,0.12)' : '1px solid var(--silver-light)';

  return (
    <div>
      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', borderBottom: tabBorder, paddingBottom: 0, marginBottom: '1.5rem' }}>
        {[
          { id: 'vehicle', label: 'By Vehicle' },
          { id: 'partname', label: 'By Part Name' },
        ].map(tab => (
          <button key={tab.id}
            onClick={() => { setActiveTab(tab.id); setError(''); }}
            style={{
              fontFamily: '"Barlow Condensed"', fontSize: '0.9rem', fontWeight: 700,
              letterSpacing: '0.06em', textTransform: 'uppercase',
              padding: '0.6rem 1.2rem', border: 'none', background: 'none', cursor: 'pointer',
              color: activeTab === tab.id ? tabColor : (dark ? 'var(--silver)' : 'var(--text-light)'),
              borderBottom: `2px solid ${activeTab === tab.id ? tabActiveBorder : 'transparent'}`,
              marginBottom: -1,
              transition: 'color 0.2s, border-color 0.2s',
            }}
          >{tab.label}</button>
        ))}
      </div>

      {/* Vehicle Tab */}
      {activeTab === 'vehicle' && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: compact ? '1fr 1fr' : 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: '1rem', alignItems: 'end',
        }}>
          <div>
            <label className="form-label" style={{ color: labelColor }}>Year</label>
            <SelectInput value={ps.year} onChange={ps.setYear} dark={dark}>
              <option value="">Select Year</option>
              {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
            </SelectInput>
          </div>
          <div>
            <label className="form-label" style={{ color: labelColor }}>Make</label>
            <SelectInput value={ps.make} onChange={ps.setMake} dark={dark}>
              <option value="">Select Make</option>
              {ps.makes.map(m => <option key={m} value={m}>{m}</option>)}
            </SelectInput>
          </div>
          {!compact && (
            <div>
              <label className="form-label" style={{ color: labelColor }}>Model</label>
              <SelectInput value={ps.model} onChange={ps.setModel} dark={dark}>
                <option value="">Select Model</option>
                {ps.models.map(m => <option key={m} value={m}>{m}</option>)}
              </SelectInput>
            </div>
          )}
          {!compact && (
            <div>
              <label className="form-label" style={{ color: labelColor }}>Category</label>
              <SelectInput value={ps.category} onChange={ps.setCategory} dark={dark}>
                {PART_CATEGORIES_LIST.map(c => <option key={c} value={c}>{c}</option>)}
              </SelectInput>
            </div>
          )}
          <div style={{ alignSelf: 'flex-end' }}>
            <button onClick={handleVehicleSearch} className="btn-dark" style={{
              width: '100%', justifyContent: 'center',
              background: dark ? 'var(--orange)' : 'var(--navy)',
              borderColor: dark ? 'var(--orange)' : 'var(--navy)',
            }}>
              🔍 Search Parts
            </button>
          </div>
        </div>
      )}

      {/* Part Name Tab */}
      {activeTab === 'partname' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '1rem', alignItems: 'end' }}>
          <div>
            <label className="form-label" style={{ color: labelColor }}>Part Name or Number</label>
            <TextInput
              value={ps.partName}
              onChange={ps.setPartName}
              placeholder="e.g. alternator, 2018 Honda door panel, OEM headlight..."
              dark={dark}
            />
          </div>
          <div style={{ alignSelf: 'flex-end' }}>
            <button onClick={handlePartSearch} className="btn-dark" style={{
              background: dark ? 'var(--orange)' : 'var(--navy)',
              borderColor: dark ? 'var(--orange)' : 'var(--navy)',
              whiteSpace: 'nowrap',
            }}>
              🔍 Search
            </button>
          </div>
        </div>
      )}

      {error && (
        <p style={{ color: '#e05252', fontSize: '0.85rem', marginTop: '0.75rem' }}>{error}</p>
      )}
    </div>
  );
}
